import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { FormDialogComponent } from '../../dialog-templates/form-dialog/form-dialog.component';
import { NgForm } from '@angular/forms';

import { AnalyticsService } from '../../services/analytics.service';
import { EVENT_SUPPORT_CANCELED, EVENT_SUPPORT_OPENED } from '../../analytics/events';
import { FormDialog } from '../../types';
import { UiDialogService } from '../../ui/dialog/dialog.service';
import { SendSupportRequest } from '../../generated-api/models';
import { SupportService } from '../../services/support.service';

@Component({
  selector: 'app-support',
  templateUrl: './support.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportComponent {
  private analytics = inject(AnalyticsService);
  private dialog = inject(UiDialogService);
  private router = inject(Router);

  private readonly support = inject(SupportService);
  private readonly sendSupportRequestMutation = injectMutation(() => this.support.sendSupportRequestMutationOptions());

  openSupportDialog() {
    this.analytics.sendEvent(EVENT_SUPPORT_OPENED);

    const data: FormDialog = {
      formType: 'textArea',
      title: 'Get help',
      message: `Tell us what's going on and we'll get back to you by email.`,
      submitText: 'Send',
      form: {
        label: null,
        placeholder: 'What do you need help with?',
        maxlength: false,
      },
      success: {
        title: 'Thanks for reaching out!',
        message: "We've got your message and will get back to you soon.",
      },
      onSubmit: (form: NgForm) => this.sendSupportRequest(form),
      onCancel: () => this.analytics.sendEvent(EVENT_SUPPORT_CANCELED),
    };

    this.dialog.open(FormDialogComponent, {
      data,
    });
  }

  sendSupportRequest(form: NgForm): Promise<unknown> {
    const message = form.controls['inputValue']?.value;
    if (!message) return Promise.resolve();

    // Context is captured at submit time so the page/viewport reflect what the
    // user was actually looking at, and the replay URL points at the session
    // that led here.
    const data: SendSupportRequest = {
      message,
      context: this.captureContext(),
    };

    return this.sendSupportRequestMutation.mutateAsync(data);
  }

  private captureContext(): SendSupportRequest['context'] {
    const distinctId = this.analytics.getDistinctId();
    const replayUrl = this.analytics.getSessionReplayUrl();
    return {
      // Origin + path only: query strings and fragments can carry tokens
      // (Plaid OAuth, password reset) that must not land in a support inbox.
      pageUrl: window.location.origin + window.location.pathname,
      route: this.router.url.replace(/[?#].*$/, ''),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
      ...(distinctId ? { posthogDistinctId: distinctId } : {}),
      ...(replayUrl ? { posthogSessionReplayUrl: replayUrl } : {}),
    };
  }
}
